import { useEditorState, type Editor } from '@tiptap/react';
import { ListIcon } from 'lucide-react';
import { useMemo } from 'react';

import { DropdownMenuWrapper, useDropdownMenuState } from '@/components/ui/tiptap/tiptap-ui-primitives';
import { useTiptapEditor } from '@/hooks/tiptap';
import { isNodeInSchema, shouldShow } from '@/lib/tiptap';

import { ListButton, type ListDropdownMenuProps, listOptions, type ListType } from '.';

function canToggleAnyList(editor: Editor | null, listTypes: ListType[]): boolean {
  if (!editor) return false;
  return listTypes.some((type) => canToggleList(editor, type));
}
function canToggleList(editor: Editor | null, type: ListType): boolean {
  if (!editor) {
    return false;
  }
  switch (type) {
    case 'bulletList':
      return editor.can().toggleBulletList();
    case 'orderedList':
      return editor.can().toggleOrderedList();
    case 'taskList':
      return editor.can().toggleList('taskList', 'taskItem');
    default:
      return false;
  }
}

function isActive(editor: Editor | null, type: ListType): boolean {
  if (!editor) return false;

  switch (type) {
    case 'bulletList':
      return editor.isActive('bulletList');
    case 'orderedList':
      return editor.isActive('orderedList');
    case 'taskList':
      return editor.isActive('taskList');
    default:
      return false;
  }
}

function useIsAnyListActive(editor: Editor | null, types: ListType[]): boolean {
  return (
    useEditorState({
      editor,
      selector: (ctx) => {
        return types.some((type) => ctx.editor?.isActive(type));
      },
    }) ?? false
  );
}

export function ListDropdownMenu({ editor: providedEditor, types = ['bulletList', 'orderedList', 'taskList'], onOpenChange, ...props }: ListDropdownMenuProps) {
  const editor: Editor | null = useTiptapEditor(providedEditor);
  const isAnyActive: boolean = useIsAnyListActive(editor, types);
  const inSchema: boolean = types.some((type) => isNodeInSchema(type, editor));
  const { isOpen, filteredItems, handleOpenChange, activeIcon } = useDropdownMenuState<ListType>(editor, types, listOptions, isActive, <ListIcon />);

  const show: boolean = useMemo(() => {
    return shouldShow([inSchema, canToggleAnyList(editor, types)]);
  }, [inSchema]);

  if (!show || !editor || !editor.isEditable) {
    return null;
  }

  return (
    <DropdownMenuWrapper isOpen={isOpen} onOpenChange={(open) => handleOpenChange(open, onOpenChange)} ariaLabel="List options" buttonProps={props} activeIcon={activeIcon()} isActive={isAnyActive}>
      <>
        {filteredItems.map((option) => (
          <ListButton key={option.type} editor={editor} type={option.type} />
        ))}
      </>
    </DropdownMenuWrapper>
  );
}
